import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectWell } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { useGetWellsQuery } from "@api/mApi";
import { useGetWellsReportRecencyQuery } from "@api/pApi";

/**
 * Well switcher — moved out of the (now-removed) top header into the top of the
 * left nav, under the wordmark. Expanded: a full-width button showing the
 * current well. Collapsed: a compact square avatar (the well's initial) that
 * still opens the same popover. The popover lists every well; picking one drives
 * every screen through wellContext.
 */
export function WellSwitcher({ collapsed }: { collapsed: boolean }) {
  const dispatch = useAppDispatch();
  const { t } = useLocale();
  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  const { data: wells = [] } = useGetWellsQuery();
  const { data: recency = [] } = useGetWellsReportRecencyQuery();

  // Last-activity date per well (latest daily-report date). Drives the sort +
  // the date shown in the menu + the "most recent" hint.
  const lastByUwi = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of recency) if (r.last_report) m.set(r.well_uwi, r.last_report);
    return m;
  }, [recency]);
  const sortedWells = useMemo(() => {
    const rank = (uwi: string) => lastByUwi.get(uwi) ?? "";
    return [...wells].sort((a, b) => {
      const ra = rank(a.uwi), rb = rank(b.uwi);
      if (ra !== rb) return ra < rb ? 1 : -1; // most-recent activity first
      return a.commonName.localeCompare(b.commonName);
    });
  }, [wells, lastByUwi]);
  // The well with the most recent activity overall — the "where to go" hint.
  const mostRecentUwi = sortedWells.find((w) => lastByUwi.get(w.uwi))?.uwi;

  const current = sortedWells.find((w) => w.id === selectedWellId) ?? sortedWells[0];
  const currentName = current?.commonName ?? "—";
  const initial = currentName.trim().charAt(0).toUpperCase() || "•";

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="well-switcher-wrap" ref={rootRef}>
      <button
        type="button"
        className={`well-switcher${collapsed ? " collapsed" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t("topbar.wellSwitcher")}
        title={collapsed ? `${t("topbar.wellSwitcher")}: ${currentName}` : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        {collapsed ? (
          <span className="well-avatar" aria-hidden="true">
            {initial}
          </span>
        ) : (
          <>
            <span className="well-avatar" aria-hidden="true">
              {initial}
            </span>
            <span className="well-name">{currentName}</span>
            <span className="chev" aria-hidden="true">
              ▾
            </span>
          </>
        )}
      </button>

      {open && (
        <ul className="well-menu" role="listbox" id={menuId} aria-label={t("well.switcher.title")}>
          {sortedWells.length === 0 && <li className="well-menu-empty">{t("common.na")}</li>}
          {sortedWells.map((w) => {
            const last = lastByUwi.get(w.uwi);
            const isRecent = w.uwi === mostRecentUwi;
            const cls = [w.id === current?.id ? "active" : "", isRecent ? "recent" : ""]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={w.id} role="option" aria-selected={w.id === current?.id}>
                <button
                  type="button"
                  className={cls}
                  onClick={() => {
                    dispatch(selectWell(w.id));
                    setOpen(false);
                  }}
                >
                  <span className="well-menu-name">
                    {isRecent && <span className="well-dot" aria-hidden="true">●</span>}
                    {w.commonName}
                  </span>
                  <span className="well-menu-meta">
                    <span className="well-menu-uwi">{w.uwi}</span>
                    {last && <span className="well-menu-date" title={t("well.lastActivity")}>{last}</span>}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
